import "../../../css/common.scss";
import "../../../css/tailwind.css";
import bp from '../../core/bp.js';
import { flyToStartupCoordinates } from '../../core/cameraPositioningUtil';
import { assembleMap } from "../../core/mapFactory";
import qp from '../../core/queriablePromises.js';
import styling from '../../core/styling.js';
import displayModes from '../../core/displayModes.js';
import controlPanelCctr from '../../composite/ControlPanel.js';
import { createChart } from '../../charts/stackedBar';
import { createEventLog } from '../../eventlogs/eventLog';
import { create, createDiv } from '../../core/domMan';

class EngineDefinition {

    /*
     * Used by stage, isPlaying.
     */
    isOn = false;

    /*
     * Tracks if map is fully initialized, and ready to be used.
     */
    isMapInitialized = false;
    mapInfo;

    /**
     * Additional values supplied.
     */
    properties = null;

    /**
     * Max query loading time (seconds)
     */
    maxQueryTime = 10;

    /*
     * Directs if current instance is being played now.
     */

    radrBoxDatasource = null;

    stage = {
        isPlaying: () => this.isOn,
        hide: () => {
            this.isOn = false;
            const { titleBar } = this.controlPanel;
            titleBar.playPauseBtn.value = 'Play';
            this.hide();
        },
        enter: () => {
            if (this.isOn == false) {
                const { titleBar } = this.controlPanel;
                titleBar.playPauseBtn.value = 'Pause';
                this.isOn = true;
                this.show();
            }
        }
    };

    constructor(args, mapName) {
        Object.defineProperties(this, {
            id: { value: args.id },
            mapName: { value: mapName },
            placeholder: { value: args.placeholder },
            controlPanel: { value: args.controlPanel ?? controlPanelCctr() }
        });

        const { dataLoading, updateIntervalSeconds, mapConfig, zoombox, debugMode, autoPlay, mapTitle, properties, identifier, pitch, renderMode } = args;
        let displayedMode;

        if (args.skipBaseInit) {
            displayedMode = {};
        }
        else {
            displayedMode = displayModes.extract(args);
            if (dataLoading == undefined)
                throw new Error("No 'dataLoading' was supplied");
        }

        this.properties = properties || {};
        this.state = {
            identifier,
            debugMode,
            autoPlay,
            mapConfig,
            zoombox,
            updateIntervalSeconds,
            dataLoading,
            displayedMode,
            mapTitle: mapTitle ?? displayedMode.description,
            properties: this.properties,
            pitch: pitch ?? 0,
            renderMode: renderMode ?? 'flat'
        };
    }

    hidePresetTexts() {
        const { titleBar, title, playPauseBtn, footerTitle } = this.controlPanel.titleBar;
        const { zone, cleanup } = this.controlPanel.contentBox;
        const { statusBar } = this.controlPanel;
        cleanup();
        let elementsToHide = [titleBar, title, zone, statusBar.center];
        if (!this.state.debugMode) {
            elementsToHide.push(playPauseBtn, footerTitle);
        }
        styling.hide(elementsToHide);
    }

    getViewIndex(view) {
        return this.views.indexOf(view);
    }

    /** Sets last updated, used for debug purposes mostly. */
    debugDataWasUpdated() {
        const { titleBar } = this.controlPanel;
        const { dataLastUpdated, debugMode } = this.state;
        debugMode && (titleBar.footerTitle.innerHTML = `Updated at ${dataLastUpdated?.toISOString() ?? '[Unknown]'}`);
    }

    setActive() {
        const { titleBar } = this.controlPanel;
        const { mapTitle, debugMode } = this.state;
        this.hidePresetTexts();

        this.debugDataWasUpdated();
        titleBar.setSubTitle(mapTitle);
        document.title = mapTitle;

        styling.show([titleBar.titleBar, titleBar.subTitle]);
        if (debugMode) {
            titleBar.playPauseBtn.onclick = () => titleBar.playPause(this.stage);
            titleBar.playPauseBtn.disabled = '';
            styling.show(titleBar.playPauseBtn);
        }

        if (this.produceAgenda) {
            this.produceAgenda();
        }

        const { contextchartdata, eventlogdata, displayedMode } = this.state;

        if (contextchartdata) {
            const { contentBox, statusBar } = this.controlPanel;
            const { center, zone } = contentBox;
            createChart(center, contextchartdata, displayedMode, statusBar.center);
            styling.show([zone, statusBar.center]);
        }

        if (eventlogdata) {
            const { contentBox } = this.controlPanel;
            const { right, zone } = contentBox;

            createEventLog(right, eventlogdata, displayedMode);

            styling.show([zone, right]);
        }

        if (this.state.map && !this.state.defaultCamera) {
            this.state.defaultCamera = this.state.map.getCamera()
        }

        flyToStartupCoordinates(this.state);

        if (this.isMapInitialized) {
            this.displayState();
            this.stage.enter();
        }
    };

    buildMap() {
        const { mapConfig } = this.state;

        const freshMap = this.placeholder ? assembleMap(this.placeholder, mapConfig) : assembleMap(this.mapName, mapConfig);
        return new Promise((next, fail) => freshMap.events.add('ready', () => {
            this.onMapReady(freshMap);
            this.setActive();
            next();
        }));
    }

    onMapReady(freshMap) {
        this.state.map = freshMap;
        this.onMapInitialized();
    }

    /** To call when map init finished. */
    onMapInitialized() {
        this.isMapInitialized = true;
        // this.displayState();
    }

    show() { return; };
    hide() { throw new Error('Please implement what to do when engine leaves the stage'); };

    displayState() { return; };

    async initialDataLoad() {
        const result = await this.loadDataPromise(this.maxQueryTime * 1000)

        if (result.status != 200) throw Error("Data fetch failed")
        const data = await result.json()

        this.setDataLoaded(data);
    };

    setDataLoaded(loadedData) {
        const { featureCollection, props } = loadedData;
        const { mapConfig: freshPositioning, contextchartdata, eventlogdata } = props ?? {};

        if (freshPositioning?.zoom) {
            const { mapConfig } = this.state;
            const { zoom, longitude, latitude, minZoom, maxZoom } = freshPositioning;
            Object.assign(mapConfig, { zoom, longitude, latitude, minZoom, maxZoom });
        }

        this.state.displayedData = featureCollection;
        this.state.contextchartdata = contextchartdata;
        this.state.eventlogdata = eventlogdata;
        this.state.dataLastUpdated = new Date();
        this.state.dataVersion ??= 0;
        this.state.dataVersion += 1;

        if (this.stage.isPlaying()) {
            this.debugDataWasUpdated();
        }
    };

    async loadData(maxLoadSeconds) {
        const response = await this.loadDataPromise(maxLoadSeconds)

        if (!response || response.status != 200) {
            return false
        }

        const data = await response.json()
        this.setDataLoaded(data);

        return true
    };

    async loadDataPromise(timeout) {
        const { dataLoading } = this.state;
        const { liveEndpoint } = dataLoading;

        return (liveEndpoint !== null && typeof liveEndpoint === 'object')
            ? new Promise((resolve, reject) => resolve(liveEndpoint))
            : bp.FetchWithTimeout(liveEndpoint, timeout);
    }

    execute() {
        const engine = this;
        return this.initialDataLoad().then(please => engine.buildMap()).then(please => {
            if (engine.state.autoPlay) {
                engine.stage.enter();
            }
        });
    }

    getCssClasses = (pointInfo) => {
        const color = pointInfo || 'unknown';
        return `dataPoint ${color} `;
    }

    produceAgenda() {
        const { contentBox } = this.controlPanel;
        const { zone, left, cleanup } = contentBox;
        cleanup();

        if (!!left) {
            const agendaZone = create(left, 'div', { classList: 'agenda grid' });

            const { statusLabels } = this.legend;

            if (!!statusLabels) {
                statusLabels.forEach(element => {
                    const { status, explain } = element;
                    const cssClasses = this.getCssClasses(status);
                    const ph = createDiv(agendaZone, { className: 'inline-flex items-center m-1 min-w-fit' });
                    const dataPoint = createDiv(ph, { className: `${cssClasses} p-1 shrink-0` });
                    const explaination = create(ph, 'p', { className: 'mx-1', innerText: explain });
                });

                styling.show([zone, left]);
            }
        }
    }

    get legend() {
        const { legend } = this.properties;
        return legend || {};
    };
};

export default EngineDefinition;
